import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { RobotIcon, SparkleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { useAskWorkbookAgent } from './useAskWorkbookAgent';
import type { AutomationsSectionProps } from './workbookIntegrations';

/** Agents' Automations section: the Agent definitions this Workbook is a tool of, and a handoff to the agent. */
export function WorkbookAgentsSection({ editor }: AutomationsSectionProps) {
  const gt = useGT();
  const agent = useAskWorkbookAgent(editor.workbookId);
  const automations = useQuery(
    orpc.workbooks.automations.queryOptions({
      input: { workbookId: editor.workbookId },
    }),
  );
  const agents = automations.data?.agents ?? [];
  return (
    <section
      aria-labelledby="workbook-automations-agents"
      className="space-y-2 rounded-lg border p-3"
    >
      <h3
        id="workbook-automations-agents"
        className="flex items-center gap-2 text-sm font-medium"
      >
        <RobotIcon className="size-4 text-muted-foreground" />
        <T context="workbook automations agents section">Agents</T>
      </h3>
      <p className="text-sm text-muted-foreground">
        <T context="workbook automations agents help">
          Attach this workbook to an agent in the agent&apos;s Tools tab to let
          it run the workbook and read its results. The assistant can also list,
          run and read your workbooks from anywhere in the app.
        </T>
      </p>
      {agents.length > 0 && (
        <ul className="divide-y rounded-md border">
          {agents.map((item) => (
            <li key={item.agentDefinitionId} className="px-2 py-1.5 text-sm">
              <Link
                to="/agents/$agentDefinitionId"
                params={{ agentDefinitionId: item.agentDefinitionId }}
                className="font-medium underline-offset-2 hover:underline"
              >
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={!agent.ready || agent.busy}
        onClick={() =>
          void agent.ask(
            gt(
              'Explain what this workbook does, step by step, and how its latest run went.',
              { context: 'workbook ask agent about the workbook' },
            ),
          )
        }
      >
        <SparkleIcon />
        <T context="workbook ask agent about this workbook">
          Ask agent about this workbook
        </T>
      </Button>
    </section>
  );
}
